import type { FieldGroupDefinition, MapDocument, PropertySchema, ValidationIssue } from "daedalus-core";
import { GATE } from "../editor/type-names.ts";

/**
 * A gate that closes itself as content (P1.8-T18, ADR-058's amendment of 2026-10-31, decisions 1 and 10): the six
 * authored fields, their limits, how a hand-edited value reads, and the Issues the editor and the compile raise for
 * one. The fields live in the server chunk with the rest of a gate's fields; no page learns them. A gate with none of
 * them reads every condition off, so every gate of every published world behaves as it did. Pure.
 */

/**
 * The key a refused `open` or `close` of a gate sends in `interaction.text` when the push-out cannot place everyone in its
 * gateway, which the page words ("The gate won't budge."). It blames nobody: the gate is never refused because someone is
 * in the way.
 */
export const STUCK_LINE = "thing.stuck";

/**
 * How a key unlocks a gate: for everyone for good (`village`), for one open only (`each`), or for the character who
 * spent it for good (`character`).
 */
export type UnlockMode = "village" | "each" | "character";

/**
 * The unlock modes, in the order the inspector offers them; the first is what a gate without the field does.
 */
export const UNLOCK_MODES: readonly UnlockMode[] = ["village", "each", "character"];

/**
 * The least and greatest of each numeric field when it is on. `closeAfter` and `closeWhenEmpty` are off at 0; `closeDelay`
 * takes 0.
 */
export const AUTO_CLOSE_LIMITS = {
  closeAfter: { min: 2, max: 3600 },
  closeWhenEmpty: { min: 1, max: 32 },
  closeDelay: { min: 0, max: 30 },
} as const;

/**
 * A gate's six fields as the inspector draws them. The limits are checked by `readAutoClose` and `autoCloseIssues`, not
 * by the schema, whose subset has no greatest value and whose keywords a contract reader built earlier would refuse.
 */
export const AUTO_CLOSE_FIELDS: { readonly [name: string]: PropertySchema } = {
  unlockMode: { type: "string", default: "village", enum: UNLOCK_MODES },
  closeAfter: { type: "integer", default: 0, minimum: 0 },
  closeBehind: { type: "boolean", default: false },
  closeOnLeave: { type: "boolean", default: false },
  closeWhenEmpty: { type: "integer", default: 0, minimum: 0 },
  closeDelay: { type: "integer", default: 0, minimum: 0 },
};

/**
 * The id of the inspector group the four conditions and the wait are drawn in.
 */
export const AUTO_CLOSE_GROUP = "closes";

/**
 * The inspector group "Closes by itself" with its two presets: "Door behind me" (`closeBehind` on, a wait of 3 seconds)
 * and "Timed" (`closeAfter` 10). A preset writes exactly its fields and nothing else.
 */
export const AUTO_CLOSE_GROUPS: readonly FieldGroupDefinition[] = [
  {
    id: AUTO_CLOSE_GROUP,
    fields: ["closeAfter", "closeBehind", "closeOnLeave", "closeWhenEmpty", "closeDelay"],
    presets: [
      { id: "behind", values: { closeBehind: true, closeDelay: 3 } },
      { id: "timed", values: { closeAfter: 10 } },
    ],
  },
];

/**
 * A gate's auto-close fields as read: each condition (off as 0 or false), the delay, the unlock mode, and the names of
 * the fields whose value was not one a field takes, which read as off.
 */
export interface AutoClose {
  readonly closeAfter: number;
  readonly closeBehind: boolean;
  readonly closeOnLeave: boolean;
  readonly closeWhenEmpty: number;
  readonly closeDelay: number;
  readonly unlockMode: UnlockMode;
  readonly bad: readonly string[];
}

type Fields = { readonly [key: string]: unknown };
type Limited = keyof typeof AUTO_CLOSE_LIMITS;

/**
 * Reads one whole-number field.
 *
 * @param fields The gate's fields.
 * @param name The field.
 * @param bad The names of the fields found bad, added to.
 * @returns The value, or 0 for an absent or bad one.
 */
function whole(fields: Fields, name: Limited, bad: string[]): number {
  const value = fields[name];
  if (value === undefined) {
    return 0;
  }
  const { min, max } = AUTO_CLOSE_LIMITS[name];
  if (typeof value === "number" && Number.isInteger(value) && (value === 0 || (value >= min && value <= max))) {
    return value;
  }
  bad.push(name);
  return 0;
}

/**
 * Reads one boolean field.
 *
 * @param fields The gate's fields.
 * @param name The field.
 * @param bad The names of the fields found bad, added to.
 * @returns The value, or false for an absent or bad one.
 */
function flag(fields: Fields, name: string, bad: string[]): boolean {
  const value = fields[name];
  if (value === undefined) {
    return false;
  }
  if (typeof value === "boolean") {
    return value;
  }
  bad.push(name);
  return false;
}

/**
 * The auto-close fields a gate's behaviour fields make. A value a field does not take, which only a hand edit writes,
 * reads as off for that field and is named in `bad`, so the gate fails safe as a plain gate.
 *
 * @param fields The gate's behaviour fields.
 * @returns The reading.
 * @example
 * `readAutoClose({ closeBehind: true, closeDelay: 3 })` reads closeBehind on and closeDelay 3, every other condition off,
 * unlockMode `village` and no bad field.
 */
export function readAutoClose(fields: Fields): AutoClose {
  const bad: string[] = [];
  const mode = fields["unlockMode"];
  const known = UNLOCK_MODES.find((entry) => entry === mode);
  if (mode !== undefined && known === undefined) {
    bad.push("unlockMode");
  }
  const closeAfter = whole(fields, "closeAfter", bad);
  const closeBehind = flag(fields, "closeBehind", bad);
  const closeOnLeave = flag(fields, "closeOnLeave", bad);
  const closeWhenEmpty = whole(fields, "closeWhenEmpty", bad);
  const closeDelay = whole(fields, "closeDelay", bad);
  return { closeAfter, closeBehind, closeOnLeave, closeWhenEmpty, closeDelay, unlockMode: known ?? "village", bad };
}

/**
 * Whether a gate has any condition on, so the map server arms it when it opens. `closeDelay` and `unlockMode` alone arm
 * nothing.
 *
 * @param read The gate's reading.
 * @returns True when one of the four conditions is on.
 */
export function autoCloseOf(read: AutoClose): boolean {
  return read.closeAfter > 0 || read.closeBehind || read.closeOnLeave || read.closeWhenEmpty > 0;
}

/**
 * What each field takes, as the Issue about a bad value states it after the field's name.
 */
const TAKES: Readonly<Record<string, string>> = {
  closeAfter: "0 or 2 to 3600 whole seconds",
  closeBehind: "on or off",
  closeOnLeave: "on or off",
  closeWhenEmpty: "0 or 1 to 32 whole tiles",
  closeDelay: "0 to 30 whole seconds",
  unlockMode: "village, each or character",
};

/**
 * The Issues a map's gates raise in the editor and at a compile: a value no field takes (`autoclose.bad-field`, which
 * reads as off), a wait that only a timed close is on to ignore (`autoclose.no-delay-effect`, a warning), and an unlock
 * mode other than `village` on a gate with no Lock (`autoclose.unlock-no-lock`, a warning). Each states its fix after the
 * semicolon.
 *
 * @param map The map.
 * @param locked Whether a gate's fields make a lock; the lock's own module answers it.
 * @returns The issues, in document order.
 */
export function autoCloseIssues(map: MapDocument, locked: (fields: Fields) => boolean): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour?.type !== GATE) {
        continue;
      }
      const fields = prop.behaviour.fields;
      const read = readAutoClose(fields);
      const at = { mapId: map.id, layerId: layer.id, propId: prop.id };
      for (const name of read.bad) {
        issues.push({ ...at, kind: "autoclose.bad-field", message: `prop ${prop.id} is a Gate whose ${name} must be ${TAKES[name]}; set it again, or empty it` });
      }
      if (read.closeDelay > 0 && read.closeAfter > 0 && !read.closeBehind && !read.closeOnLeave && read.closeWhenEmpty === 0) {
        issues.push({ ...at, kind: "autoclose.no-delay-effect", severity: "warning", ignorable: true, message: `prop ${prop.id} is a Gate whose closeDelay does nothing with only closeAfter on; turn on another condition, or set closeDelay to 0` });
      }
      if (read.unlockMode !== "village" && !locked(fields)) {
        issues.push({ ...at, kind: "autoclose.unlock-no-lock", severity: "warning", ignorable: true, message: `prop ${prop.id} is a Gate with an unlock mode and no Lock; name its Lock, or set the mode back to village` });
      }
    }
  }
  return issues;
}
